import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './gallery-showcase.css'

import Img1 from '../../../assets/img/gallery-new/lab-gallery-1.webp'
import Img2 from '../../../assets/img/gallery-new/lab-gallery-2.webp'
import Img3 from '../../../assets/img/gallery-new/lab-gallery-3.webp'
import Img4 from '../../../assets/img/gallery-new/lab-gallery-4.webp'
import Img5 from '../../../assets/img/gallery-new/lab-gallery-5.webp'
import Img6 from '../../../assets/img/gallery-new/lab-gallery-6.webp'
import Img7 from '../../../assets/img/gallery-new/lab-gallery-7.webp'
import Img8 from '../../../assets/img/gallery-new/lab-gallery-8.webp'

const GALLERY_DATA = [
    { id: 1, img: Img1, category: 'Classroom', title: 'Interactive Lecture', caption: 'Faculty conducting an interactive classroom session for pharmacy students.' },
    { id: 2, img: Img2, category: 'Classroom', title: 'Classroom Session', caption: 'Students attending a lecture in one of the institute\'s spacious classrooms.' },
    { id: 3, img: Img3, category: 'Laboratory', title: 'Practical Lab Session', caption: 'Students at work across the benches during a Pharmaceutical Analysis practical.' },
    { id: 4, img: Img4, category: 'Laboratory', title: 'Group Practical Session', caption: 'A student group discussing results during a Pharmacognosy lab session.' },
    { id: 5, img: Img5, category: 'Laboratory', title: 'Reagent Analysis', caption: 'Preparing and measuring reagents as part of a Pharmaceutical Analysis practical.' },
    { id: 6, img: Img6, category: 'Laboratory', title: 'Titration Practical', caption: 'Students performing a titration experiment, reading the burette scale carefully.' },
    { id: 7, img: Img7, category: 'Laboratory', title: 'Sterilization Practical', caption: 'Hands-on training with an autoclave during a sterilization techniques session.' },
    { id: 8, img: Img8, category: 'Laboratory', title: 'Volumetric Analysis', caption: 'Students carrying out a volumetric analysis experiment in the Pharmaceutical Chemistry lab.' },
]

const CATEGORIES = ['All', 'Laboratory', 'Classroom']

function GalleryShowcase() {
    const [activeCategory, setActiveCategory] = useState('All')
    const [lightboxIndex, setLightboxIndex] = useState(null)
    const touchStartX = useRef(0)
    const touchEndX = useRef(0)

    const filteredData = activeCategory === 'All'
        ? GALLERY_DATA
        : GALLERY_DATA.filter(item => item.category === activeCategory)

    const openLightbox = (id) => {
        const idx = filteredData.findIndex(item => item.id === id)
        setLightboxIndex(idx)
    }

    const closeLightbox = () => setLightboxIndex(null)

    const showNext = useCallback(() => {
        setLightboxIndex(prev => (prev === null ? null : (prev + 1) % filteredData.length))
    }, [filteredData.length])

    const showPrev = useCallback(() => {
        setLightboxIndex(prev => (prev === null ? null : (prev - 1 + filteredData.length) % filteredData.length))
    }, [filteredData.length])

    // Keyboard navigation
    useEffect(() => {
        if (lightboxIndex === null) return
        const handleKey = (e) => {
            if (e.key === 'Escape') closeLightbox()
            else if (e.key === 'ArrowRight') showNext()
            else if (e.key === 'ArrowLeft') showPrev()
        }
        window.addEventListener('keydown', handleKey)
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', handleKey)
            document.body.style.overflow = ''
        }
    }, [lightboxIndex, showNext, showPrev])

    const handleTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX }
    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].screenX
        const delta = touchStartX.current - touchEndX.current
        if (delta > 50) showNext()
        else if (delta < -50) showPrev()
    }

    const activeItem = lightboxIndex !== null ? filteredData[lightboxIndex] : null

    return (
        <section className="aip-gallery pt-120 pb-120">
            <div className="container">

                {/* Section heading */}
                <div className="aip-gallery-heading text-center mb-50">
                    <span className="aip-gallery-eyebrow">Campus Moments</span>
                    <h2>A Glimpse Into Campus Life</h2>
                    <p>Explore moments from our labs, classrooms and student life at Anjanadri Institute of Pharmacy.</p>
                </div>

                {/* Filter buttons */}
                <div className="aip-gallery-filters">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            className={`aip-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {activeCategory === cat && (
                                <motion.span
                                    layoutId="aip-filter-pill"
                                    className="aip-filter-pill"
                                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                                />
                            )}
                            <span className="aip-filter-label">{cat}</span>
                        </button>
                    ))}
                </div>

                {/* Masonry grid — whole grid cross-fades on filter change, items stagger in */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCategory}
                        className="aip-gallery-masonry"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={{
                            hidden: {},
                            visible: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
                            exit: { transition: { staggerChildren: 0.025, staggerDirection: -1 } },
                        }}
                    >
                        {filteredData.map((item) => (
                            <motion.div
                                key={item.id}
                                className="aip-gallery-item"
                                variants={{
                                    hidden: { opacity: 0, y: 18, scale: 0.97 },
                                    visible: {
                                        opacity: 1, y: 0, scale: 1,
                                        transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
                                    },
                                    exit: {
                                        opacity: 0, y: 10, scale: 0.97,
                                        transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
                                    },
                                }}
                                onClick={() => openLightbox(item.id)}
                            >
                                <img src={item.img} alt={item.title} loading="lazy" />
                                <div className="aip-gallery-overlay">
                                    <span className="aip-gallery-tag">{item.category}</span>
                                    <h4>{item.title}</h4>
                                    <div className="aip-gallery-zoom">
                                        <i className="fal fa-search-plus" />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {activeItem && (
                    <motion.div
                        className="aip-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeLightbox}
                    >
                        <button className="aip-lightbox-close" onClick={closeLightbox} aria-label="Close">
                            <i className="fal fa-times" />
                        </button>

                        <button
                            className="aip-lightbox-nav aip-lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); showPrev() }}
                            aria-label="Previous image"
                        >
                            <i className="fal fa-angle-left" />
                        </button>

                        <motion.div
                            className="aip-lightbox-content"
                            key={activeItem.id}
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            transition={{ duration: 0.25 }}
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            <img src={activeItem.img} alt={activeItem.title} />
                            <div className="aip-lightbox-caption">
                                <h4>{activeItem.title}</h4>
                                <p>{activeItem.caption}</p>
                                <span className="aip-lightbox-counter">{lightboxIndex + 1} / {filteredData.length}</span>
                            </div>
                        </motion.div>

                        <button
                            className="aip-lightbox-nav aip-lightbox-next"
                            onClick={(e) => { e.stopPropagation(); showNext() }}
                            aria-label="Next image"
                        >
                            <i className="fal fa-angle-right" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    )
}

export default GalleryShowcase
